import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { ComposerStateService } from '@app/model-composer';
import { CustomizationRowComponent } from '@app/model-composer/components/customization-row.component';
import { ElementOptionalService, OptionalState } from '@app/model-composer/services/element-optional.service';
import {TranslatePipe} from "@ngx-translate/core";

@Component({
  selector: 'app-element-customization',
  standalone: true,
  imports: [CustomizationRowComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="ipe:h-full ipe:flex ipe:flex-col ipe:flex-1 ipe:min-h-0">
      <div class="ipe:flex-1 ipe:min-h-0 ipe:overflow-y-auto ipe:pb-4">
        @if (activeItem(); as item) {
          <div class="ipe:px-6 ipe:pt-6 ipe:space-y-5">
            <div class="ipe:flex ipe:items-start ipe:justify-between ipe:gap-4">
              <div class="ipe:min-w-0">
                <div class="ipe:text-sm ipe:tracking-[0.15em] ipe:text-gray-400 font-walter">
                  {{'composition.selectedElement' | translate }}
                </div>
                <div class="ipe:mt-1 ipe:text-sm ipe:text-gray-800 ipe:font-medium ipe:truncate">
                  {{ item.element.name ?? ('ID ' + item.element.id) }}
                </div>
                @if (item.element.dimensions) {
                  <div class="ipe:text-sm ipe:text-gray-400">
                    {{ item.element.dimensions }}
                  </div>
                }
              </div>
              @if (item.element.image) {
                <img
                  [src]="item.element.image"
                  class="ipe:w-14 ipe:h-14 ipe:rounded-md ipe:object-cover ipe:bg-[#f6f3f1]"
                />
              }
            </div>
            @if (item?.element?.customization?.length > 0 ) {
              @for (customization of item.element.customization; track customization.id) {
                @if (customization?.type === 'OPTIONAL' && isCustomizationVisible(item.uid, customization, item.element.customization)) {
                  @let opt = optionalState(item.uid, customization);
                  <div class="optional-panel ipe:bg-white ipe:border ipe:border-[#ede8e3] ipe:rounded-sm">
                    <div class="ipe:px-4 ipe:py-4 ipe:flex ipe:items-center ipe:justify-between ipe:gap-4">
                      <div class="ipe:min-w-0">
                        <div class="ipe:text-sm ipe:tracking-[0.15em] ipe:text-gray-800 font-walter">
                          {{ customization.label }}
                        </div>
                      </div>
                      <div class="ipe:flex ipe:items-center ipe:gap-3 ipe:shrink-0">
                        @if (opt.enabled && maxQty(customization) > 1) {
                          <div class="ipe:flex ipe:items-center ipe:gap-2">
                            <button
                              type="button"
                              class="ipe:w-8 ipe:h-8 ipe:flex ipe:items-center ipe:justify-center
                                   ipe:border ipe:border-[#ede8e3] ipe:bg-white
                                   ipe:hover:bg-[#f6f3f1] ipe:transition-colors ipe:cursor-pointer"
                              (click)="decQty(item.uid, customization, item.element.customization)"
                              [disabled]="opt.quantity <= 1"
                              [class.ipe:opacity-40]="opt.quantity <= 1"
                              [class.ipe:cursor-not-allowed]="opt.quantity <= 1"
                            >
                              −
                            </button>
                            <span class="ipe:min-w-8 ipe:text-center ipe:text-sm ipe:font-semibold ipe:text-gray-800">
                              {{ opt.quantity }}
                            </span>
                            <button
                              type="button"
                              class="ipe:w-8 ipe:h-8 ipe:flex ipe:items-center ipe:justify-center
                                   ipe:border ipe:border-[#ede8e3] ipe:bg-white
                                   ipe:hover:bg-[#f6f3f1] ipe:transition-colors ipe:cursor-pointer"
                              (click)="incQty(item.uid, customization, item.element.customization)"
                              [disabled]="opt.quantity >= maxQty(customization)"
                              [class.ipe:opacity-40]="opt.quantity >= maxQty(customization)"
                              [class.ipe:cursor-not-allowed]="opt.quantity >= maxQty(customization)"
                            >
                              +
                            </button>
                          </div>
                        }
                        @if (customization?.optional?.deactivable) {
                          <span
                            class="ipe:inline-flex ipe:items-center ipe:justify-center
                                   ipe:min-w-[120px] ipe:px-4 ipe:py-2
                                   ipe:border ipe:text-sm ipe:tracking-[0.12em]
                                   font-walter ipe:cursor-pointer ipe:transition-all"
                                                      [class]="opt.enabled
                              ? 'ipe:bg-[#3f312f] ipe:text-white ipe:border-[#3f312f]'
                              : 'ipe:bg-transparent ipe:text-[#bca48b] ipe:border-[#bca48b]'"
                                                      (click)="toggleOptional(item.uid, customization, item.element.customization)"
                                                    >
                            {{ (opt.enabled ? 'composition.included' : 'composition.add') | translate }}
                          </span>
                        }
                      </div>
                    </div>

                    @if (opt.enabled && customization?.customizationRows?.length) {
                      <div class="optional-content ipe:space-y-4">
                        @for (row of customization.customizationRows; track row.id) {
                          @if (row?.show && row.materialOptions.length) {
                            <div class="ipe:space-y-2 ipe:px-4 ipe:pb-4 ipe:border-b ipe:border-gray-100">
                              <div class="ipe:text-sm ipe:text-gray-400 ipe:tracking-[0.15em] font-walter">
                                {{ row.name }}
                              </div>
                              <app-customization-row
                                [mode]="'element'"
                                [row]="row"
                                [scopeUid]="item.uid"
                              />
                            </div>
                          }
                        }
                      </div>
                    }
                  </div>
                }
              }
            }
          </div>
        } @else {
          <div class="ipe:h-full ipe:flex ipe:items-center ipe:justify-center ipe:text-gray-400">
            {{ 'composition.firstSelectElement' | translate }}
          </div>
        }
      </div>
    </section>
  `,
  styles: `
    .optional-panel {
      animation: optionalFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1);
      transform-origin: top;
    }

    @keyframes optionalFadeIn {
      from {
        opacity: 0;
        transform: translateY(-6px) scaleY(0.98);
      }

      to {
        opacity: 1;
        transform: translateY(0) scaleY(1);
      }
    }

    .optional-content {
      animation: revealContent 500ms ease;
    }

    @keyframes revealContent {
      from {
        opacity: 0;
        max-height: 0;
      }

      to {
        opacity: 1;
        max-height: 1000px;
      }
    }
  `
})
export class ElementCustomizationComponent {
  private readonly state = inject(ComposerStateService);
  private readonly optionalSvc = inject(ElementOptionalService);

  readonly activeItem = this.state.activeItem;

  constructor() {
    effect(() => {
      const item = this.activeItem();
      if (!item) { return; }
      this.optionalSvc.initializeDefaults(item.uid, item.element?.customization);
    });
  }

  optionalState(uid: string, customization: any): OptionalState {
    return this.optionalSvc.getState(uid, customization);
  }

  maxQty(customization: any): number {
    return this.optionalSvc.maxQty(customization);
  }

  toggleOptional(uid: string, customization: any, customizations: any): void {
    this.optionalSvc.toggleOptional(uid, customization, customizations);
  }

  incQty(uid: string, customization: any, customizations: any): void {
    this.optionalSvc.incQty(uid, customization, customizations);
  }

  decQty(uid: string, customization: any, customizations: any): void {
    this.optionalSvc.decQty(uid, customization, customizations);
  }

  isCustomizationVisible(uid: string, customization: any, customizations: any): boolean {
    return this.optionalSvc.isCustomizationVisible(uid, customization, customizations);
  }
}
